
import Vue from 'vue';
import OccupancyReports from '@/components/reports/OccupancyReports.vue';
import { Room, Zone } from '@/interfaces/lighting';
import { RoomsFloors, RoomID } from '@/interfaces/reports';
import { Site } from '@/interfaces/multiTenancy';
import { Validation } from 'vuelidate/vuelidate';
import { Floor } from '@/interfaces/locations';
import { SelectMenu } from '@/interfaces/global';
import weekOfYear from '@/plugins/dayjs';
import dayjs from '@/plugins/dayjs';
import { AxiosError } from 'axios';

interface ComponentData {
	occupancySelected: boolean;
	selectedFloor: string | null;
	selectedRoom: [{ id: number | null; name: string | null }];
	results: boolean;
	weekSelector: {
		selectedWeekOne: {
			week: number | null;
			year: number | null;
			dateFrom: dayjs.Dayjs | null;
			dateTo: dayjs.Dayjs | null;
		};
		selectedWeekTwo: {
			week: number | null;
			year: number | null;
			dateFrom: dayjs.Dayjs | null;
			dateTo: dayjs.Dayjs | null;
		};
	};
}

interface Luminaires {
	id: number;
}

export default Vue.extend({
	components: {
		OccupancyReports,
	},
	data(): ComponentData {
		return {
			results: false,
			occupancySelected: false,
			selectedFloor: null,
			selectedRoom: [{ id: null, name: '' }],
			weekSelector: {
				selectedWeekOne: { week: null, year: null, dateFrom: null, dateTo: null },
				selectedWeekTwo: { week: null, year: null, dateFrom: null, dateTo: null },
			},
		};
	},

	computed: {
		selectedSite(): Site | null {
			return this.$store.getters['tenancy/sites/getSelectedSite'];
		},
		zones(): Zone[] | [] {
			return this.$store.getters['locations/zones/getZones'];
		},
		floors(): Floor[] {
			return this.$store.getters['locations/floors/getFloors'];
		},
		rooms(): Room[] {
			return this.$store.getters['locations/rooms/getRooms'];
		},
		reports() {
			return this.$store.getters['reports/reports/getOccupancyReports'];
		},
		roomsInFloors(): RoomsFloors[] {
			return this.$store.getters['locations/rooms/getRooms'].map((r: Room) => {
				if (r.fk_area === this.currentFloor?.id) {
					return {
						name: r.name,
						roomId: r.id,
					};
				}
			});
		},

		getRoomNames(): RoomID[] {
			return this.roomsInFloors
				.filter((def) => def !== undefined)
				.map((room) => {
					return {
						name: room.name,
						id: room.roomId,
					};
				});
		},

		getRoomIds(): number[] {
			return this.roomsInFloors.filter((und) => und !== undefined).map((nm) => nm.roomId);
		},

		floorOptions(): SelectMenu[] {
			const options: SelectMenu[] = [
				{ value: null, text: this.$t('Select Floor'), disabled: true },
			];

			for (const floor of this.floors) {
				options.push({ value: floor.name, text: floor.name });
			}
			return options;
		},

		roomOptions(): SelectMenu[] {
			const options: SelectMenu[] = [];

			for (const room of this.getRoomNames) {
				options.push({ value: { id: room.id, name: room.name }, text: room.name });
			}
			return options;
		},

		weekNumberOptions(): SelectMenu[] {
			let weekTo = weekOfYear().week();
			let oneYearAgo = weekOfYear(dayjs().subtract(12, 'month')).week();
			let yearTo = dayjs().endOf('year').week();
			let yearOfOneYearAgo = dayjs().subtract(12, 'month').year();
			let currentYear = dayjs().year();
			let weekOY, wOY, wOYS, wOYE;

			const options: SelectMenu[] = [
				{
					value: { week: null, year: null, dateFrom: null, dateTo: null },
					text: this.$t('Please select week nr'),
					disabled: true,
				},
			];
			weekOY = weekOfYear();
			for (let weeks = weekTo; weeks > 0; weeks--) {
				wOY = weekOY.week(weeks).year(currentYear);
				wOYS = wOY.startOf('week');
				wOYE = wOY.endOf('week');

				options.push({
					value: { week: weeks, year: currentYear, dateFrom: wOYS, dateTo: wOYE },
					text: `Week ${weeks} of ${currentYear} : ${wOYS.year()}.${
						wOYS.month() + 1
					}.${wOYS.date()} - ${wOYE.year()}.${wOYE.month() + 1}.${wOYE.date()}`,
				});
			}
			for (let weeks = yearTo; weeks >= oneYearAgo; weeks--) {
				wOY = weekOY.week(weeks).year(yearOfOneYearAgo);
				wOYS = wOY.startOf('week');
				wOYE = wOY.endOf('week');

				options.push({
					value: { week: weeks, year: yearOfOneYearAgo, dateFrom: wOYS, dateTo: wOYE },
					text: `Week ${weeks} of ${yearOfOneYearAgo} : ${wOYS.year()}.${
						wOYS.month() + 1
					}.${wOYS.date()} - ${wOYE.year()}.${wOYE.month() + 1}.${wOYE.date()}`,
				});
			}
			return options;
		},

		currentFloor(): Floor | undefined {
			return this.floors.filter((floor) => this.selectedFloor === floor.name)[0];
		},
		getRoomZones(): Zone[] {
			return this.$store.getters['locations/zones/getRoomZones'];
		},
	},

	validations: {
		selectedFloor: {
			required: function (): boolean {
				if (
					this.weekSelector.selectedWeekOne &&
					this.weekSelector.selectedWeekTwo &&
					this.selectedFloor
				) {
					return true;
				}
				return false;
			},
		},
		selectedRoom: {
			required: function (): boolean {
				if (
					this.weekSelector.selectedWeekOne &&
					this.weekSelector.selectedWeekTwo &&
					this.selectedFloor
				) {
					return true;
				}
				return false;
			},
		},
		weekSelector: {
			selectedWeekOne: {
				required: function (): boolean {
					if (this.weekSelector.selectedWeekOne === this.weekSelector.selectedWeekTwo) {
						return false;
					} else if (
						this.weekSelector.selectedWeekOne &&
						!this.weekSelector.selectedWeekTwo &&
						this.selectedFloor
					) {
						return false;
					}
					return true;
				},
			},
			selectedWeekTwo: {
				required: function (): boolean {
					if (this.weekSelector.selectedWeekTwo === this.weekSelector.selectedWeekOne) {
						return false;
					} else if (
						this.weekSelector.selectedWeekTwo &&
						!this.weekSelector.selectedWeekOne &&
						this.selectedFloor
					) {
						return false;
					}
					return true;
				},
			},
		},
	},

	methods: {
		validateState(validation: Validation): boolean | null {
			const { $dirty, $error } = validation;
			return $dirty ? !$error : null;
		},
		occupancySelect() {
			this.occupancySelected = true;
		},

		getZoneNames(roomId: (number | null)[]) {
			const rooms: (number | null)[] = roomId[0] === null ? this.getRoomIds : roomId;
			let zone = [];
			for (const room of rooms) {
				const zones = this.$store.getters['locations/zones/getRoomZones'](room).map(
					(z: Luminaires) => z.id
				);
				zone.push(...zones);
			}
			return zone;
		},

		getOccupancyFromZone(roomId: (number | null)[]) {
			const zones = this.getZoneNames(roomId.length === 0 ? [null] : roomId);
			this.fetchOccupancy(zones);
		},

		changeResult() {
			this.results = false;
		},

		getLuminairesInArea(zoneId: number) {
			return this.$store.getters['locations/zones/getZoneLuminaires'](zoneId).map(
				(e: Luminaires) => e.id
			);
		},

		getMovementSensorsInArea(zoneId: number) {
			return this.$store.getters['locations/zones/getZoneMovementSensors'](zoneId).map(
				(e: Luminaires) => [e.id, zoneId]
			);
		},

		formatDate(date: dayjs.Dayjs | null): string | null {
			return date === null
				? null
				: `${date.year()}-${
						date.month() + 1
				  }-${date.date()} ${date.hour()}:${date.minute()}:${date.second()}`;
		},

		async generateReport() {
			if (
				this.selectedFloor &&
				(this.weekSelector.selectedWeekOne.week || this.weekSelector.selectedWeekTwo.week)
			) {
				this.getOccupancyFromZone(this.selectedRoom.map((e) => e.id));
				this.results = true;
			}
		},

		async fetchOccupancy(zones: number[]): Promise<void> {
			try {
				const devices: number[][] = [];
				const oneFrom = this.weekSelector?.selectedWeekOne.dateFrom;
				const oneTo = this.weekSelector?.selectedWeekOne.dateTo;
				const twoFrom = this.weekSelector?.selectedWeekTwo.dateFrom;
				const twoTo = this.weekSelector?.selectedWeekTwo.dateTo;

				await this.$store.dispatch('reports/reports/cleanAllReports');

				for (const zone of zones) {
					const sensors = this.getMovementSensorsInArea(zone);
					for (const devs of sensors) {
						devices.push(devs);
					}
				}

				await this.$store.dispatch('reports/reports/fetchOccupancyReport', {
					params: {
						devices: devices,
						zones: zones,
						start: this.formatDate(oneFrom),
						end: this.formatDate(oneTo),
						start2: this.formatDate(twoFrom),
						end2: this.formatDate(twoTo),
					},
				});
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-occupancy-reports',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
		},
	},
});
